import { useState, useEffect, createContext, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileText,
  Newspaper,
  Image,
  Settings,
  Menu as MenuIcon,
  LogOut,
  Navigation,
  Tag,
  MessageSquare,
  Inbox,
  ChevronRight,
  X,
  Briefcase,
  Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider
export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
      } catch (error) {
        localStorage.removeItem("admin_token");
        setToken(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const newToken = res.data.token;
    localStorage.setItem("admin_token", newToken);
    setToken(newToken);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("admin_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page
export const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/monkey/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials");
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-its-teal flex items-center justify-center p-4">
      <div className="glass-card w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="heading-3 mb-2">Admin Login</h1>
          <p className="text-its-offwhite/60 text-sm">Sign in to access the CMS</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-md text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="admin-email" className="form-label">Email</label>
            <input
              type="email"
              id="admin-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="form-input"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="form-label">Password</label>
            <input
              type="password"
              id="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="form-input"
              data-testid="admin-login-password"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            data-testid="admin-login-submit"
            className="btn-primary w-full disabled:opacity-50"
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/" className="text-its-teal-light hover:text-its-gold text-sm">
            ← Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

// Sidebar Navigation
const SidebarNav = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const allNavItems = [
    { path: "/monkey/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/monkey/pages", label: "Pages", icon: FileText, roles: ["admin", "editor"] },
    { path: "/monkey/blogs", label: "Blog Posts", icon: Newspaper, roles: ["admin", "editor"] },
    { path: "/monkey/topics", label: "Topics", icon: Tag, roles: ["admin", "editor"] },
    { path: "/monkey/galleries", label: "Photo Galleries", icon: Image, roles: ["admin", "editor"] },
    { path: "/monkey/navigation", label: "Navigation", icon: Navigation, roles: ["admin"] },
    { path: "/monkey/service-boxes", label: "Service Boxes", icon: Briefcase, roles: ["admin", "editor"] },
    { path: "/monkey/submissions", label: "Form Submissions", icon: MessageSquare, roles: ["admin", "editor"] },
    { path: "/monkey/users", label: "Users", icon: Users, roles: ["admin"] },
  ];

  const navItems = allNavItems.filter(
    (item) => !item.roles || item.roles.includes(user?.role)
  );

  const handleLogout = async () => {
    await logout();
    navigate("/monkey");
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-its-teal-dark border-r border-its-teal-light/20 z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-its-teal-light/20 flex items-center justify-between">
            <Link to="/monkey/dashboard" className="flex items-center gap-2">
              <span className="font-semibold text-white">It's IT</span>
              <span className="text-xs px-2 py-0.5 bg-its-gold/20 text-its-gold rounded">CMS</span>
            </Link>
            <button onClick={onClose} className="lg:hidden text-its-offwhite hover:text-white">
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  location.pathname === item.path
                    ? "bg-its-gold/20 text-its-gold"
                    : "text-its-offwhite hover:bg-its-teal-light/10 hover:text-white"
                }`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-its-teal-light/20 space-y-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-its-offwhite hover:bg-its-teal-light/10 hover:text-white transition-colors"
            >
              <ChevronRight size={18} />
              View Website
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-red-400 hover:bg-red-500/10 w-full transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Admin Layout
export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/monkey");
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-its-teal flex items-center justify-center">
        <div className="animate-pulse text-its-offwhite">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-its-teal">
      <SidebarNav isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-its-teal/95 backdrop-blur-sm border-b border-its-teal-light/20">
          <div className="flex items-center justify-between px-4 h-14">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-its-offwhite hover:text-white"
            >
              <MenuIcon size={24} />
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-its-offwhite">Welcome, {user.name}</span>
              <span className="text-xs px-2 py-0.5 bg-its-teal-secondary/30 text-its-teal-light rounded">
                {user.role}
              </span>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
};
